/**
 * RPN Calculator PWA - Main Entry Point
 * Modernized from the 2011 BlackBerry PlayBook calculator
 *
 * @author Marco van Hylckama Vlieg
 * @version 2.0.0
 */

import './styles/main.css';
import { Calculator, type KeyStroke, CONVERSION_PROPERTIES } from './calculator';
import { Keyboard, Router, renderDisplay, renderStackInfo } from './ui';
import type { PageId } from './ui/Router';

class App {
  private calculator: Calculator;
  private keyboard: Keyboard | null = null;
  private router: Router;

  // DOM elements
  private displayElement: HTMLElement | null = null;
  private stackInfoElement: HTMLElement | null = null;
  private keyboardElement: HTMLElement | null = null;
  private calculatorElement: HTMLElement | null = null;

  constructor() {
    this.calculator = new Calculator();
    this.router = new Router(this.onPageChange.bind(this));

    // Set up mode change callbacks
    this.calculator.setModeChangeCallback(this.onModeChange.bind(this));

    // Initialize app
    this.render();
    this.bindEvents();

    // Best-fit the calculator to the viewport
    this.fitCalculator();
    window.addEventListener('resize', () => this.fitCalculator());
    screen.orientation?.addEventListener('change', () => this.fitCalculator());

    // Initialize router
    this.router.init();

    // Update display with current calculator state
    this.updateDisplay(this.calculator.doCommand('refresh').display);
  }

  /**
   * Render the main app HTML
   */
  private render(): void {
    const app = document.getElementById('app');
    if (!app) return;

    app.innerHTML = `
      <!-- Calculator Page -->
      <div class="calculator" id="calculator-page">
        <div class="display-metal">
          <div class="lcd-display dotmatrix">
            <div class="stack-info" id="stackinfo" aria-live="polite"></div>
            <div class="first-line" id="firstline" role="status" aria-label="Calculator display"></div>
            <div class="modes" role="status" aria-label="Calculator modes">
              <div id="mode_deg" role="img" aria-label="Degrees mode"></div>
              <div id="mode_rad" class="${this.calculator.currentTrigMode === 'rad' ? 'on' : ''}" role="img" aria-label="Radians mode"></div>
              <div id="mode_grad" role="img" aria-label="Gradians mode"></div>
              <div id="mode_f" role="img" aria-label="F mode"></div>
              <div id="mode_g" role="img" aria-label="G mode"></div>
              <div id="mode_fix" class="${this.calculator.currentDisplayMode === 'fix' ? 'on' : ''}" role="img" aria-label="Fixed precision"></div>
              <div id="mode_sci" class="${this.calculator.currentDisplayMode === 'sci' ? 'on' : ''}" role="img" aria-label="Scientific notation"></div>
              <div id="mode_eng" class="${this.calculator.currentDisplayMode === 'eng' ? 'on' : ''}" role="img" aria-label="Engineering notation"></div>
              <div id="mode_hyp" role="img" aria-label="Hyperbolic mode"></div>
              <div id="mode_sto" role="img" aria-label="Store mode"></div>
            </div>
            <div class="gloss"></div>
          </div>
          <div class="logo" id="logo" role="button" aria-label="About" tabindex="0"></div>
        </div>
        <div class="keyboard" id="keyboard">
          ${this.renderKeyboard()}
        </div>
      </div>

      <!-- About Page -->
      <div class="page" id="about-page" aria-hidden="true">
        <div class="page-bg-pattern"></div>
        <header class="relative z-10 backdrop-blur-md bg-zinc-950/60 border-b border-white/5 px-5 py-4 flex items-center">
          <button class="back-button group flex items-center gap-2 bg-white/5 hover:bg-white/10 text-zinc-400 hover:text-zinc-100 rounded-xl px-4 py-2 text-sm font-medium transition-all duration-200 border border-white/5 hover:border-white/10" data-page="calculator" aria-label="Back to calculator">
            <svg class="w-4 h-4 transition-transform duration-200 group-hover:-translate-x-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7"/></svg>
            Back
          </button>
          <h1 class="flex-1 text-center text-sm font-medium tracking-wide text-zinc-400 uppercase mr-16">About</h1>
        </header>
        <div class="relative z-10 max-w-xl mx-auto px-5 py-12">
          <div class="flex flex-col items-center text-center mb-8">
            <div class="relative mb-6">
              <div class="absolute inset-0 rounded-2xl bg-blue-500/20 blur-xl scale-150"></div>
              <img src="/images/logo.png" alt="RPN Calculator" class="relative w-24 h-24 rounded-2xl shadow-2xl ring-1 ring-white/10">
            </div>
            <h2 class="text-2xl font-bold text-white tracking-tight mb-2">RPN Calculator</h2>
            <div class="inline-flex items-center gap-2 bg-gradient-to-r from-blue-500/20 to-violet-500/20 rounded-full px-4 py-1 border border-blue-500/20 mb-1">
              <div class="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></div>
              <span class="text-xs font-semibold text-blue-300">Version 2.0.0</span>
            </div>
          </div>

          <div class="glass-card gradient-border rounded-2xl p-6 space-y-4">
            <div class="flex items-center gap-3 pb-4 border-b border-white/5">
              <div class="w-8 h-8 rounded-lg bg-violet-500/10 flex items-center justify-center flex-shrink-0">
                <svg class="w-4 h-4 text-violet-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/></svg>
              </div>
              <p class="text-zinc-300 text-sm">&copy; 2011-2024 Marco van Hylckama Vlieg</p>
            </div>
            <p class="text-zinc-400 text-sm leading-relaxed">This software is provided 'as is'. While the utmost care has been taken to make this calculator perform as accurately as possible there may be issues causing wrong results to be displayed.</p>
            <p class="text-zinc-500 text-xs leading-relaxed">Because of the nature of JavaScript's floating point math, results can also be subject to subtle rounding errors. Because of this there is <span class="text-zinc-300 font-medium">NO WARRANTY</span>. Use at your own risk.</p>
          </div>
        </div>
      </div>

      <!-- Preferences Page -->
      <div class="page" id="preferences-page" aria-hidden="true">
        <div class="page-bg-pattern"></div>
        <header class="relative z-10 backdrop-blur-md bg-zinc-950/60 border-b border-white/5 px-5 py-4 flex items-center">
          <button class="back-button group flex items-center gap-2 bg-white/5 hover:bg-white/10 text-zinc-400 hover:text-zinc-100 rounded-xl px-4 py-2 text-sm font-medium transition-all duration-200 border border-white/5 hover:border-white/10" data-page="calculator" aria-label="Back to calculator">
            <svg class="w-4 h-4 transition-transform duration-200 group-hover:-translate-x-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7"/></svg>
            Back
          </button>
          <h1 class="flex-1 text-center text-sm font-medium tracking-wide text-zinc-400 uppercase mr-16">Preferences</h1>
        </header>
        <div class="relative z-10 max-w-lg mx-auto px-5 py-10">
          <form id="prefs-form" class="space-y-4">
            <p class="text-xs font-medium tracking-widest text-zinc-500 uppercase mb-1 px-1">Display</p>
            <div class="glass-card rounded-2xl divide-y divide-white/5">
              <div class="flex items-center justify-between px-5 py-4 group">
                <div class="flex items-center gap-3">
                  <div class="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center flex-shrink-0 transition-colors group-hover:bg-blue-500/15">
                    <svg class="w-4 h-4 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M4 6h16M4 12h16M4 18h7"/></svg>
                  </div>
                  <div>
                    <label for="displaystack-toggle" class="text-sm font-medium text-zinc-200 block cursor-pointer">Stack Info</label>
                    <span class="text-xs text-zinc-500">Show stack values in display</span>
                  </div>
                </div>
                <div class="toggle ${this.calculator.isDisplayStackEnabled ? 'on' : ''}" id="displaystack-toggle" role="switch" aria-checked="${this.calculator.isDisplayStackEnabled}" tabindex="0"></div>
              </div>
              <div class="flex items-center justify-between px-5 py-4 group">
                <div class="flex items-center gap-3">
                  <div class="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center flex-shrink-0 transition-colors group-hover:bg-amber-500/15">
                    <svg class="w-4 h-4 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"/></svg>
                  </div>
                  <div>
                    <label for="audio-toggle" class="text-sm font-medium text-zinc-200 block cursor-pointer">Key Sounds</label>
                    <span class="text-xs text-zinc-500">Audible click feedback</span>
                  </div>
                </div>
                <div class="toggle ${this.calculator.isAudioFeedbackEnabled ? 'on' : ''}" id="audio-toggle" role="switch" aria-checked="${this.calculator.isAudioFeedbackEnabled}" tabindex="0"></div>
              </div>
            </div>

            <p class="text-xs font-medium tracking-widest text-zinc-500 uppercase mt-6! mb-1 px-1">Formatting</p>
            <div class="glass-card rounded-2xl px-5 py-4">
              <div class="flex items-center gap-3 mb-3">
                <div class="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
                  <svg class="w-4 h-4 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14"/></svg>
                </div>
                <div>
                  <label for="commas-select" class="text-sm font-medium text-zinc-200 block">Number Format</label>
                  <span class="text-xs text-zinc-500">Separator and decimal style</span>
                </div>
              </div>
              <select id="commas-select" name="commas" class="styled-select w-full bg-zinc-800/80 border border-white/10 text-zinc-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500/30 cursor-pointer">
                <option value="1" ${this.calculator.currentCommaType === '1' ? 'selected' : ''}>No separators, comma before fraction</option>
                <option value="2" ${this.calculator.currentCommaType === '2' ? 'selected' : ''}>No separators, dot before fraction</option>
                <option value="3" ${this.calculator.currentCommaType === '3' ? 'selected' : ''}>Comma as separator, dot before fraction</option>
                <option value="4" ${this.calculator.currentCommaType === '4' ? 'selected' : ''}>Dot as separator, comma before fraction</option>
              </select>
            </div>
          </form>
        </div>
      </div>

      <!-- Manual Page -->
      <div class="page" id="manual-page" aria-hidden="true">
        <div class="page-bg-pattern"></div>
        <header class="relative z-10 backdrop-blur-md bg-zinc-950/60 border-b border-white/5 px-5 py-4 flex items-center">
          <button class="back-button group flex items-center gap-2 bg-white/5 hover:bg-white/10 text-zinc-400 hover:text-zinc-100 rounded-xl px-4 py-2 text-sm font-medium transition-all duration-200 border border-white/5 hover:border-white/10" data-page="calculator" aria-label="Back to calculator">
            <svg class="w-4 h-4 transition-transform duration-200 group-hover:-translate-x-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7"/></svg>
            Back
          </button>
          <h1 class="flex-1 text-center text-sm font-medium tracking-wide text-zinc-400 uppercase mr-16">Manual</h1>
        </header>
        <div class="relative z-10 max-w-2xl mx-auto px-5 py-8">
          <div class="glass-card rounded-2xl p-6 sm:p-8 max-h-[calc(100vh-110px)] overflow-y-auto">
            ${this.renderManual()}
          </div>
        </div>
      </div>

      <!-- Stack Explorer Page -->
      <div class="page" id="stack-page" aria-hidden="true">
        <div class="page-bg-pattern"></div>
        <header class="relative z-10 backdrop-blur-md bg-zinc-950/60 border-b border-white/5 px-5 py-4 flex items-center">
          <button class="back-button group flex items-center gap-2 bg-white/5 hover:bg-white/10 text-zinc-400 hover:text-zinc-100 rounded-xl px-4 py-2 text-sm font-medium transition-all duration-200 border border-white/5 hover:border-white/10" data-page="calculator" aria-label="Back to calculator">
            <svg class="w-4 h-4 transition-transform duration-200 group-hover:-translate-x-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7"/></svg>
            Back
          </button>
          <h1 class="flex-1 text-center text-sm font-medium tracking-wide text-zinc-400 uppercase mr-16">Stack Explorer</h1>
        </header>
        <div class="relative z-10 max-w-lg mx-auto px-5 py-10">
          <div class="flex items-center gap-2 mb-5 px-1">
            <div class="w-6 h-6 rounded-md bg-blue-500/10 flex items-center justify-center">
              <svg class="w-3.5 h-3.5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"/></svg>
            </div>
            <p class="text-xs font-medium tracking-widest text-zinc-500 uppercase">Registers</p>
          </div>
          <ul id="stack-list" class="space-y-2"></ul>
        </div>
      </div>

      <!-- Converter Configuration Page -->
      <div class="page" id="converter-page" aria-hidden="true">
        <div class="page-bg-pattern"></div>
        <header class="relative z-10 backdrop-blur-md bg-zinc-950/60 border-b border-white/5 px-5 py-4 flex items-center">
          <button class="back-button group flex items-center gap-2 bg-white/5 hover:bg-white/10 text-zinc-400 hover:text-zinc-100 rounded-xl px-4 py-2 text-sm font-medium transition-all duration-200 border border-white/5 hover:border-white/10" data-page="calculator" aria-label="Back to calculator">
            <svg class="w-4 h-4 transition-transform duration-200 group-hover:-translate-x-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7"/></svg>
            Back
          </button>
          <h1 class="flex-1 text-center text-sm font-medium tracking-wide text-zinc-400 uppercase mr-16">Converter</h1>
        </header>
        <div class="relative z-10 max-w-lg mx-auto px-5 py-10 space-y-4">
          <div class="glass-card rounded-2xl px-5 py-4 flex items-start gap-3">
            <div class="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
              <svg class="w-4 h-4 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
            </div>
            <div>
              <p class="text-zinc-300 text-sm leading-relaxed">Configure the unit converter. Press <span class="kbd">CONV</span> to perform conversions.</p>
              <p class="text-zinc-500 text-xs mt-1">Changes are saved automatically when you go back.</p>
            </div>
          </div>

          <form id="conv-form" class="space-y-3">
            <div class="glass-card rounded-2xl px-5 py-4">
              <div class="flex items-center gap-3 mb-3">
                <div class="w-8 h-8 rounded-lg bg-violet-500/10 flex items-center justify-center flex-shrink-0">
                  <svg class="w-4 h-4 text-violet-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"/></svg>
                </div>
                <label for="cproperty" class="text-sm font-medium text-zinc-200">Property</label>
              </div>
              <select id="cproperty" name="cproperty" class="styled-select w-full bg-zinc-800/80 border border-white/10 text-zinc-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500/30 cursor-pointer">
                ${CONVERSION_PROPERTIES.map((p, i) => `<option value="${i}">${p.name}</option>`).join('')}
              </select>
            </div>

            <div class="glass-card rounded-2xl px-5 py-4">
              <div class="flex items-center gap-3 mb-3">
                <div class="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
                  <svg class="w-4 h-4 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M7 16V4m0 0L3 8m4-4l4 4"/></svg>
                </div>
                <label for="cfrom" class="text-sm font-medium text-zinc-200">From</label>
              </div>
              <select id="cfrom" name="cfrom" class="styled-select w-full bg-zinc-800/80 border border-white/10 text-zinc-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500/30 cursor-pointer"></select>
            </div>

            <div class="flow-arrow">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 5v14m0 0l-4-4m4 4l4-4"/></svg>
            </div>

            <div class="glass-card rounded-2xl px-5 py-4">
              <div class="flex items-center gap-3 mb-3">
                <div class="w-8 h-8 rounded-lg bg-orange-500/10 flex items-center justify-center flex-shrink-0">
                  <svg class="w-4 h-4 text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M17 8V20m0 0l4-4m-4 4l-4-4"/></svg>
                </div>
                <label for="cto" class="text-sm font-medium text-zinc-200">To</label>
              </div>
              <select id="cto" name="cto" class="styled-select w-full bg-zinc-800/80 border border-white/10 text-zinc-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500/30 cursor-pointer"></select>
            </div>
          </form>
        </div>
      </div>
    `;

    // Get DOM references
    this.displayElement = document.getElementById('firstline');
    this.stackInfoElement = document.getElementById('stackinfo');
    this.keyboardElement = document.getElementById('keyboard');
    this.calculatorElement = document.getElementById('calculator-page');

    // Register pages with router
    this.router.registerPage('calculator', document.getElementById('calculator-page')!);
    this.router.registerPage('about', document.getElementById('about-page')!);
    this.router.registerPage('preferences', document.getElementById('preferences-page')!);
    this.router.registerPage('manual', document.getElementById('manual-page')!);
    this.router.registerPage('stack', document.getElementById('stack-page')!);
    this.router.registerPage('converter', document.getElementById('converter-page')!);

    // Initialize trig mode indicator
    this.updateModeIndicator(this.calculator.currentTrigMode, true);
  }

  /**
   * Render keyboard HTML
   */
  private renderKeyboard(): string {
    return `
      <div class="labels">
        <div class="label"></div>
        <div class="label"></div>
        <div class="label"></div>
        <div class="label"></div>
        <div class="label"></div>
        <div class="label"></div>
        <div class="label">P<span class="i"><small>y,x</small></span></div>
        <div class="label">C<span class="i"><small>y,x</small></span></div>
        <div class="label">L.R.</div>
        <div class="label">&rArr;P</div>
      </div>
      <div class="row">
        <div class="button"><div class="taparea" id="ex" role="button" aria-label="e to the x" tabindex="0"></div><div class="mainfn"><span class="i">e<span class="exp">x</span></span></div><div class="secfn">LN</div></div>
        <div class="button"><div class="taparea" id="tenx" role="button" aria-label="10 to the x" tabindex="0"></div><div class="mainfn"><span class="number">10</span><span class="i"><span class="exp">x</span></span></div><div class="secfn">LOG</div></div>
        <div class="button"><div class="taparea" id="onedivx" role="button" aria-label="1 divided by x" tabindex="0"></div><div class="mainfn"><span class="number">1</span><span class="i">/x</span></div><div class="secfn"><span class="i">y<span class="exp">x</span></span></div></div>
        <div class="button"><div class="taparea" id="xsquare" role="button" aria-label="x squared" tabindex="0"></div><div class="mainfn"><span class="i">x<span class="exp">2</span></span></div><div class="secfn"><span class="i">x<span class="exp">3</span></span></div></div>
        <div class="button"><div class="taparea" id="sqrt" role="button" aria-label="square root" tabindex="0"></div><div class="mainfn"><span class="char">&#8730;</span>&nbsp;<span class="i">x</span></div><div class="secfn"><span class="i"><span class="exp">3&nbsp;&nbsp;</span></span><span class="char">&#8730;</span>&nbsp;<span class="i">x</span></div></div>
        <div class="button"><div class="taparea" id="chs" role="button" aria-label="change sign" tabindex="0"></div><div class="mainfn">CHS</div><div class="secfn">ABS</div></div>
        <div class="button"><div class="taparea" id="seven" role="button" aria-label="7" tabindex="0"></div><div class="mainfn"><span class="number">7</span></div><div class="secfn">DEG</div></div>
        <div class="button"><div class="taparea" id="eight" role="button" aria-label="8" tabindex="0"></div><div class="mainfn"><span class="number">8</span></div><div class="secfn">RAD</div></div>
        <div class="button"><div class="taparea" id="nine" role="button" aria-label="9" tabindex="0"></div><div class="mainfn"><span class="number">9</span></div><div class="secfn">GRAD</div></div>
        <div class="button"><div class="taparea" id="divide" role="button" aria-label="divide" tabindex="0"></div><div class="mainfn"><span class="char">&#247;</span></div><div class="secfn">&rArr;RAD</div></div>
      </div>
      <div class="labels">
        <div class="label">HYP</div>
        <div class="label"></div>
        <div class="label"></div>
        <div class="label larger">&#7529;</div>
        <div class="label"></div>
        <div class="label"></div>
        <div class="label"></div>
        <div class="label"></div>
        <div class="label"></div>
        <div class="label">&rArr;R</div>
      </div>
      <div class="row">
        <div class="button"><div class="taparea" id="sin" role="button" aria-label="sine" tabindex="0"></div><div class="mainfn">SIN</div><div class="secfn">SIN<span class="exp">-1</span></div></div>
        <div class="button"><div class="taparea" id="cos" role="button" aria-label="cosine" tabindex="0"></div><div class="mainfn">COS</div><div class="secfn">COS<span class="exp">-1</span></div></div>
        <div class="button"><div class="taparea" id="tan" role="button" aria-label="tangent" tabindex="0"></div><div class="mainfn">TAN</div><div class="secfn">TAN<span class="exp">-1</span></div></div>
        <div class="button"><div class="taparea" id="pi" role="button" aria-label="pi" tabindex="0"></div><div class="mainfn"><span class="i">&#960;</span></div><div class="secfn"><span class="i larger">e</span></div></div>
        <div class="button"><div class="taparea" id="roll" role="button" aria-label="roll down" tabindex="0"></div><div class="mainfn">R&dArr;</div><div class="secfn">R&uArr;</div></div>
        <div class="button"><div class="taparea" id="eex" role="button" aria-label="exponent" tabindex="0"></div><div class="mainfn">EEX</div><div class="secfn">EEX-</div></div>
        <div class="button"><div class="taparea" id="four" role="button" aria-label="4" tabindex="0"></div><div class="mainfn"><span class="number">4</span></div><div class="secfn">FIX</div></div>
        <div class="button"><div class="taparea" id="five" role="button" aria-label="5" tabindex="0"></div><div class="mainfn"><span class="number">5</span></div><div class="secfn">SCI</div></div>
        <div class="button"><div class="taparea" id="six" role="button" aria-label="6" tabindex="0"></div><div class="mainfn"><span class="number">6</span></div><div class="secfn">ENG</div></div>
        <div class="button"><div class="taparea" id="multiply" role="button" aria-label="multiply" tabindex="0"></div><div class="mainfn"><span class="char">&#215;</span></div><div class="secfn">&rArr;DEG</div></div>
      </div>
      <div class="labels">
        <div class="label">42</div>
        <div class="label">PREFS</div>
        <div class="label"></div>
        <div class="label"></div>
        <div class="label"><span class="i"><small>&#375;</small></span>,r</div>
        <div class="label">RAN#</div>
        <div class="label"></div>
        <div class="label"></div>
        <div class="label"></div>
        <div class="label">FRAC</div>
      </div>
      <div class="row">
        <div class="button"><div class="taparea" id="xfac" role="button" aria-label="factorial" tabindex="0"></div><div class="mainfn"><span class="i">x</span>!</div></div>
        <div class="button"><div class="taparea" id="conv" role="button" aria-label="convert" tabindex="0"></div><div class="mainfn">CONV</div><div class="secfn">CCONF</div></div>
        <div class="button"><div class="taparea" id="percent" role="button" aria-label="percent" tabindex="0"></div><div class="mainfn"><span class="char">%</span></div><div class="secfn"><span class="char">&Delta;%</span></div></div>
        <div class="button"><div class="taparea" id="swapxy" role="button" aria-label="swap x and y" tabindex="0"></div><div class="mainfn"><span class="char">x&hArr;y</span></div><div class="secfn">s</div></div>
        <div class="button"><div class="taparea" id="backspace" role="button" aria-label="backspace" tabindex="0"></div><div class="mainfn">&lArr;</div><div class="secfn">CL<span class="i">x</span></div></div>
        <div class="button invisible"><div class="taparea"></div></div>
        <div class="button"><div class="taparea" id="one" role="button" aria-label="1" tabindex="0"></div><div class="mainfn"><span class="number">1</span></div><div class="secfn">&rArr;HEX</div></div>
        <div class="button"><div class="taparea" id="two" role="button" aria-label="2" tabindex="0"></div><div class="mainfn"><span class="number">2</span></div><div class="secfn">&rArr;BIN</div></div>
        <div class="button"><div class="taparea" id="three" role="button" aria-label="3" tabindex="0"></div><div class="mainfn"><span class="number">3</span></div><div class="secfn">&rArr;OCT</div></div>
        <div class="button"><div class="taparea" id="minus" role="button" aria-label="minus" tabindex="0"></div><div class="mainfn"><span class="char">-</span></div><div class="secfn">INT</div></div>
      </div>
      <div class="labels">
        <div class="label">ABOUT</div>
        <div class="label"></div>
        <div class="label"></div>
        <div class="label">MANUAL</div>
        <div class="label"></div>
        <div class="label"></div>
        <div class="label"></div>
        <div class="label"></div>
        <div class="label">CLEAR&nbsp;&#931;</div>
        <div class="label"></div>
      </div>
      <div class="row last">
        <div class="button"><div class="taparea" id="undo" role="button" aria-label="undo" tabindex="0"></div><div class="mainfn">UNDO</div><div class="secfn">STACK</div></div>
        <div class="button yellow"><div class="taparea" id="f" role="button" aria-label="F mode" tabindex="0"></div><div class="mainfn">f</div></div>
        <div class="button blue"><div class="taparea" id="g" role="button" aria-label="G mode" tabindex="0"></div><div class="mainfn">g</div></div>
        <div class="button"><div class="taparea" id="sto" role="button" aria-label="store" tabindex="0"></div><div class="mainfn">STO</div><div class="secfn">x&#772;</div></div>
        <div class="button"><div class="taparea" id="rcl" role="button" aria-label="recall" tabindex="0"></div><div class="mainfn">RCL</div><div class="secfn">CLST</div></div>
        <div class="button" id="enterbtn"><div class="taparea" id="enter" role="button" aria-label="enter" tabindex="0"></div><div class="mainfn">E<br>N<br>T<br>E<br>R</div><div class="secfn">LAST<span class="i">x</span></div></div>
        <div class="button zero"><div class="taparea" id="zero" role="button" aria-label="0" tabindex="0"></div><div class="mainfn"><span class="number">0</span></div><div class="secfn">&rArr;H.MS</div></div>
        <div class="button"><div class="taparea" id="dot" role="button" aria-label="decimal point" tabindex="0"></div><div class="mainfn"><span class="number">.</span></div><div class="secfn">&rArr;H</div></div>
        <div class="button"><div class="taparea" id="sigmaplus" role="button" aria-label="sigma plus" tabindex="0"></div><div class="mainfn"><span class="char">&#931;+</span></div><div class="secfn"><span class="char">&#931;-</span></div></div>
        <div class="button"><div class="taparea" id="plus" role="button" aria-label="plus" tabindex="0"></div><div class="mainfn"><span class="char">+</span></div><div class="secfn">RND</div></div>
      </div>
    `;
  }

  /**
   * Render manual content (simplified version)
   */
  private renderManual(): string {
    return `
      <div class="flex items-center gap-3 mb-6 pb-4 border-b border-white/5">
        <div class="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center flex-shrink-0">
          <svg class="w-4 h-4 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"/></svg>
        </div>
        <div>
          <h1 class="text-lg font-bold text-zinc-100">Introduction</h1>
        </div>
      </div>
      <p class="text-zinc-300 text-sm leading-relaxed mb-8">Welcome to the RPN Calculator! This application contains an ever growing amount of scientific and statistics functionality packed in an easy to use and attractive interface.</p>

      <h2 class="text-base font-semibold text-zinc-100 mb-3 flex items-center gap-2">
        <span class="w-1 h-5 rounded-full bg-gradient-to-b from-amber-400 to-amber-600 inline-block"></span>
        Keyboard Operation
      </h2>
      <p class="text-zinc-300 text-sm leading-relaxed mb-8">All keys on the calculator serve at least one function. Many keys have secondary or tertiary functions which can be accessed by pressing <span class="kbd kbd-f">f</span> (yellow) or <span class="kbd kbd-g">g</span> (blue) first.</p>

      <h2 class="text-base font-semibold text-zinc-100 mb-4 flex items-center gap-2">
        <span class="w-1 h-5 rounded-full bg-gradient-to-b from-blue-400 to-blue-600 inline-block"></span>
        Keyboard Shortcuts
      </h2>
      <div class="overflow-hidden rounded-xl border border-white/5 mb-8">
        <table class="w-full text-sm">
          <thead>
            <tr>
              <th class="bg-white/5 text-zinc-400 font-medium text-xs tracking-wider uppercase px-4 py-3 text-left">Key</th>
              <th class="bg-white/5 text-zinc-400 font-medium text-xs tracking-wider uppercase px-4 py-3 text-left">Function</th>
            </tr>
          </thead>
          <tbody class="text-zinc-300">
            <tr class="border-t border-white/5 hover:bg-white/[0.02] transition-colors"><td class="px-4 py-2.5"><span class="kbd">0-9</span></td><td class="px-4 py-2.5">Digits</td></tr>
            <tr class="border-t border-white/5 hover:bg-white/[0.02] transition-colors"><td class="px-4 py-2.5"><span class="kbd">.</span> <span class="kbd">,</span></td><td class="px-4 py-2.5">Decimal point</td></tr>
            <tr class="border-t border-white/5 hover:bg-white/[0.02] transition-colors"><td class="px-4 py-2.5"><span class="kbd">+</span> <span class="kbd">-</span> <span class="kbd">*</span> <span class="kbd">/</span></td><td class="px-4 py-2.5">Basic operations</td></tr>
            <tr class="border-t border-white/5 hover:bg-white/[0.02] transition-colors"><td class="px-4 py-2.5"><span class="kbd">Enter</span></td><td class="px-4 py-2.5">Push / Enter</td></tr>
            <tr class="border-t border-white/5 hover:bg-white/[0.02] transition-colors"><td class="px-4 py-2.5"><span class="kbd">Backspace</span></td><td class="px-4 py-2.5">Delete / Clear</td></tr>
            <tr class="border-t border-white/5 hover:bg-white/[0.02] transition-colors"><td class="px-4 py-2.5"><span class="kbd kbd-f">f</span></td><td class="px-4 py-2.5">F mode <span class="text-zinc-500">(yellow functions)</span></td></tr>
            <tr class="border-t border-white/5 hover:bg-white/[0.02] transition-colors"><td class="px-4 py-2.5"><span class="kbd kbd-g">g</span></td><td class="px-4 py-2.5">G mode <span class="text-zinc-500">(blue functions)</span></td></tr>
            <tr class="border-t border-white/5 hover:bg-white/[0.02] transition-colors"><td class="px-4 py-2.5"><span class="kbd">s</span></td><td class="px-4 py-2.5">SIN</td></tr>
            <tr class="border-t border-white/5 hover:bg-white/[0.02] transition-colors"><td class="px-4 py-2.5"><span class="kbd">c</span></td><td class="px-4 py-2.5">COS</td></tr>
            <tr class="border-t border-white/5 hover:bg-white/[0.02] transition-colors"><td class="px-4 py-2.5"><span class="kbd">t</span></td><td class="px-4 py-2.5">TAN</td></tr>
            <tr class="border-t border-white/5 hover:bg-white/[0.02] transition-colors"><td class="px-4 py-2.5"><span class="kbd">p</span></td><td class="px-4 py-2.5">Pi</td></tr>
            <tr class="border-t border-white/5 hover:bg-white/[0.02] transition-colors"><td class="px-4 py-2.5"><span class="kbd">e</span></td><td class="px-4 py-2.5">EEX</td></tr>
            <tr class="border-t border-white/5 hover:bg-white/[0.02] transition-colors"><td class="px-4 py-2.5"><span class="kbd">u</span></td><td class="px-4 py-2.5">Undo</td></tr>
            <tr class="border-t border-white/5 hover:bg-white/[0.02] transition-colors"><td class="px-4 py-2.5"><span class="kbd">r</span></td><td class="px-4 py-2.5">Roll</td></tr>
          </tbody>
        </table>
      </div>

      <h2 class="text-base font-semibold text-zinc-100 mb-3 flex items-center gap-2">
        <span class="w-1 h-5 rounded-full bg-gradient-to-b from-emerald-400 to-emerald-600 inline-block"></span>
        The Stack
      </h2>
      <p class="text-zinc-300 text-sm leading-relaxed mb-3">This calculator uses Reverse Polish Notation (RPN) with an unlimited stack. The stack works on a last-in, first-out basis.</p>
      <p class="text-zinc-300 text-sm leading-relaxed mb-8">The first five entries are labeled X, Y, Z, A and B. Press <span class="kbd kbd-g">g</span> then <span class="kbd">UNDO</span> to view the Stack Explorer.</p>

      <h2 class="text-base font-semibold text-zinc-100 mb-3 flex items-center gap-2">
        <span class="w-1 h-5 rounded-full bg-gradient-to-b from-violet-400 to-violet-600 inline-block"></span>
        Display Modes
      </h2>
      <p class="text-zinc-300 text-sm leading-relaxed">Press <span class="kbd kbd-g">g</span> followed by <span class="kbd">4</span>, <span class="kbd">5</span>, or <span class="kbd">6</span> and then a digit <span class="kbd">0</span>-<span class="kbd">9</span> for FIXed, SCIentific, or ENGineering mode. Press <span class="kbd">.</span> instead of a digit to return to normal mode.</p>
    `;
  }

  /**
   * Bind event listeners
   */
  private bindEvents(): void {
    if (!this.keyboardElement) return;

    // Initialize keyboard handler
    this.keyboard = new Keyboard(
      this.keyboardElement,
      this.onKeyPress.bind(this),
      {
        audioEnabled: this.calculator.isAudioFeedbackEnabled
      }
    );

    // Logo click -> About page
    document.getElementById('logo')?.addEventListener('click', () => {
      if (this.calculator.isModeF) {
        this.calculator.resetModes();
        this.router.navigateTo('about');
      }
    });

    // Back buttons
    document.querySelectorAll('.back-button').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const target = e.target as HTMLElement;
        const page = target.dataset.page as PageId;
        if (page) {
          this.savePreferences();
          this.router.navigateTo(page);
        }
      });
    });

    // Preferences toggles
    document.getElementById('displaystack-toggle')?.addEventListener('click', (e) => {
      const toggle = e.target as HTMLElement;
      toggle.classList.toggle('on');
      const isOn = toggle.classList.contains('on');
      toggle.setAttribute('aria-checked', String(isOn));
      this.calculator.setDisplayStack(isOn);
      this.updateStackInfo();
    });

    document.getElementById('audio-toggle')?.addEventListener('click', (e) => {
      const toggle = e.target as HTMLElement;
      toggle.classList.toggle('on');
      const isOn = toggle.classList.contains('on');
      toggle.setAttribute('aria-checked', String(isOn));
      this.calculator.setAudioFeedback(isOn);
      this.keyboard?.setAudioEnabled(isOn);
    });

    document.getElementById('commas-select')?.addEventListener('change', (e) => {
      const select = e.target as HTMLSelectElement;
      this.calculator.setCommaType(select.value as '1' | '2' | '3' | '4');
      this.updateDisplay(this.calculator.doCommand('refresh').display);
    });

    // Converter form: property change repopulates unit dropdowns
    document.getElementById('cproperty')?.addEventListener('change', (e) => {
      const select = e.target as HTMLSelectElement;
      this.populateUnitDropdowns(parseInt(select.value));
    });

    // Keyboard support for toggles
    document.querySelectorAll('.toggle').forEach(toggle => {
      toggle.addEventListener('keydown', (e) => {
        if ((e as KeyboardEvent).key === 'Enter' || (e as KeyboardEvent).key === ' ') {
          e.preventDefault();
          (e.target as HTMLElement).click();
        }
      });
    });
  }

  /**
   * Handle key press from keyboard handler
   */
  private onKeyPress(key: KeyStroke): void {
    // Check for special navigation keys
    if (this.calculator.isModeF) {
      if (key === 'undo') {
        this.calculator.resetModes();
        this.router.navigateTo('about');
        return;
      }
      if (key === 'conv') {
        this.calculator.resetModes();
        this.router.navigateTo('preferences');
        return;
      }
      if (key === 'sto') {
        this.calculator.resetModes();
        this.router.navigateTo('manual');
        return;
      }
    }

    if (this.calculator.isModeG) {
      if (key === 'undo') {
        this.calculator.resetModes();
        this.updateStackExplorer();
        this.router.navigateTo('stack');
        return;
      }
      if (key === 'conv') {
        this.calculator.resetModes();
        this.initConverterForm();
        this.router.navigateTo('converter');
        return;
      }
    }

    // Execute calculator command
    const result = this.calculator.doCommand(key);
    this.updateDisplay(result.display);

    // Handle special commands
    if (result.command === 'STACK_INSPECTOR') {
      this.updateStackExplorer();
      this.router.navigateTo('stack');
    }
  }

  /**
   * Update the display (deferred to next frame to keep taps responsive)
   */
  private updateDisplay(value: string): void {
    requestAnimationFrame(() => {
      if (this.displayElement) {
        this.displayElement.innerHTML = renderDisplay(value, this.calculator.currentCommaType);
      }
      this.updateStackInfo();
    });
  }

  /**
   * Update stack info display
   */
  private updateStackInfo(): void {
    if (this.stackInfoElement) {
      if (this.calculator.isDisplayStackEnabled && !this.calculator.leaveStackInfo) {
        this.stackInfoElement.innerHTML = renderStackInfo(
          this.calculator.stackCards,
          this.calculator.currentDisplayMode,
          10, // precision
          this.calculator.currentCommaType
        );
      } else if (!this.calculator.leaveStackInfo) {
        this.stackInfoElement.innerHTML = '';
      }
    }
  }

  /**
   * Update stack explorer page
   */
  private updateStackExplorer(): void {
    const list = document.getElementById('stack-list');
    if (!list) return;

    const labels = ['X', 'Y', 'Z', 'A', 'B'];
    const cards = this.calculator.stackCards;

    const badgeColors = [
      'bg-blue-500/15 text-blue-400 ring-blue-500/20',
      'bg-violet-500/15 text-violet-400 ring-violet-500/20',
      'bg-emerald-500/15 text-emerald-400 ring-emerald-500/20',
      'bg-amber-500/15 text-amber-400 ring-amber-500/20',
      'bg-rose-500/15 text-rose-400 ring-rose-500/20',
    ];

    list.innerHTML = cards.map((value, i) => {
      const label = labels[i] || `S${i - 4}`;
      const color = badgeColors[i] || badgeColors[4];
      return `<li class="glass-card rounded-xl px-4 py-3.5 flex items-center gap-4">
        <div class="register-badge ${color} ring-1">${label}</div>
        <span class="text-zinc-100 font-mono text-sm tracking-wide">${value}</span>
      </li>`;
    }).join('');
  }

  /**
   * Handle mode changes from calculator
   */
  private onModeChange(mode: string, enabled: boolean): void {
    this.updateModeIndicator(mode, enabled);
    this.updateKeyboardMode();
  }

  /**
   * Update mode indicator in display
   */
  private updateModeIndicator(mode: string, enabled: boolean): void {
    const element = document.getElementById(`mode_${mode}`);
    if (element) {
      if (enabled) {
        element.classList.add('on');
      } else {
        element.classList.remove('on');
      }
    }
  }

  /**
   * Update keyboard visual mode (f/g highlighting)
   */
  private updateKeyboardMode(): void {
    if (!this.keyboardElement) return;

    this.keyboardElement.classList.remove('f', 'g');

    if (this.calculator.isModeF) {
      this.keyboardElement.classList.add('f');
    } else if (this.calculator.isModeG) {
      this.keyboardElement.classList.add('g');
    }
  }

  /**
   * Scale calculator to best-fit the actual viewport
   */
  private fitCalculator(): void {
    if (!this.calculatorElement) return;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const scale = Math.min(1, vw / 1024, vh / 600);
    this.calculatorElement.style.transform = `scale(${scale})`;
  }

  /**
   * Populate unit dropdowns for a given property index
   */
  private populateUnitDropdowns(propertyIndex: number): void {
    const property = CONVERSION_PROPERTIES[propertyIndex];
    if (!property) return;

    const cfrom = document.getElementById('cfrom') as HTMLSelectElement;
    const cto = document.getElementById('cto') as HTMLSelectElement;
    if (!cfrom || !cto) return;

    const isTemperature = propertyIndex === 14;
    const options = property.units.map((unit, i) => {
      const value = isTemperature ? property.factors[i] : i;
      return `<option value="${value}">${unit}</option>`;
    }).join('');

    cfrom.innerHTML = options;
    cto.innerHTML = options;
  }

  /**
   * Initialize converter form with saved config
   */
  private initConverterForm(): void {
    const config = this.calculator.conversionConfig;
    const cproperty = document.getElementById('cproperty') as HTMLSelectElement;
    if (!cproperty) return;

    // Set property and populate units
    cproperty.value = String(config.propertyindex);
    this.populateUnitDropdowns(config.propertyindex);

    // Set from/to values
    const cfrom = document.getElementById('cfrom') as HTMLSelectElement;
    const cto = document.getElementById('cto') as HTMLSelectElement;
    if (cfrom) cfrom.value = String(config.fromvalue);
    if (cto) cto.value = String(config.tovalue);
  }

  /**
   * Save converter configuration from form
   */
  private saveConverterConfig(): void {
    const cproperty = document.getElementById('cproperty') as HTMLSelectElement;
    const cfrom = document.getElementById('cfrom') as HTMLSelectElement;
    const cto = document.getElementById('cto') as HTMLSelectElement;
    if (!cproperty || !cfrom || !cto) return;

    const propertyIndex = parseInt(cproperty.value);
    const property = CONVERSION_PROPERTIES[propertyIndex];
    if (!property) return;

    const isTemperature = propertyIndex === 14;
    const fromIndex = isTemperature ? cfrom.value : parseInt(cfrom.value);
    const toIndex = isTemperature ? cto.value : parseInt(cto.value);

    this.calculator.setConversionConfig({
      fromfactor: isTemperature ? cfrom.value : property.factors[fromIndex as number] as number,
      tofactor: isTemperature ? cto.value : property.factors[toIndex as number] as number,
      property: property.name,
      propertyindex: propertyIndex,
      fromvalue: fromIndex,
      tovalue: toIndex
    });
  }

  /**
   * Handle page change
   */
  private onPageChange(pageId: PageId): void {
    if (pageId === 'calculator') {
      // Refresh display when returning to calculator
      this.updateDisplay(this.calculator.doCommand('refresh').display);
    }
  }

  /**
   * Save preferences when leaving settings pages
   */
  private savePreferences(): void {
    this.saveConverterConfig();
  }
}

// Initialize app when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new App());
} else {
  new App();
}

// Lock to landscape on supported devices
(screen.orientation as any)?.lock?.('landscape').catch(() => {});
